import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles, badgeStyles } from '../styles/shared.js';
import { generationChanged, BOTTOM_PANEL_TABS } from '../utils/constants.js';
import type { BottomPanelTab } from '../utils/constants.js';
import type { ModelerController } from '../controllers/modeler-controller.js';

// ---------------------------------------------------------------------------
// <sysml-panel-zone> — Multi-tab bottom panel (VS Code-style)
//
// Hosts diagnostics, search, solver, and history tabs.
// Parent owns height; this component renders tab bar + active content.
// ---------------------------------------------------------------------------

@customElement('sysml-panel-zone')
export class SysmlPanelZone extends LitElement {
  static override styles = [
    baseStyles,
    badgeStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-top: 1px solid var(--r5-border);
      }
      .tab-bar {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: var(--r5-space-1) var(--r5-space-2);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
        background: var(--r5-surface-muted);
      }
      .pz-tab {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: var(--r5-space-1) var(--r5-space-2);
        border: none;
        border-radius: var(--r5-radius-sm);
        background: none;
        color: var(--r5-text-muted);
        font-size: var(--r5-text-xs);
        font-family: inherit;
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        white-space: nowrap;
      }
      .pz-tab:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-primary) 5%, transparent);
      }
      .pz-tab.active {
        color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
      }
      .tab-spacer { flex: 1; }
      .close-btn {
        display: flex;
        align-items: center;
        padding: var(--r5-space-1);
        border: none;
        border-radius: var(--r5-radius-sm);
        background: none;
        color: var(--r5-text-muted);
        cursor: pointer;
      }
      .close-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
      }
      .panel-content {
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      /* Drag-and-drop */
      .pz-tab[draggable="true"] { cursor: grab; }
      .pz-tab[draggable="true"]:active { cursor: grabbing; }
      :host(.drop-target) {
        outline: 2px solid var(--r5-primary);
        outline-offset: -2px;
      }

      /* Stencil WC hosts fill the content area */
      sysml-search, sysml-diagnostics {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ];

  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;
  @property({ attribute: false }) activeTab: BottomPanelTab = 'diagnostics';
  @property({ attribute: false }) theme: 'light' | 'dark' = 'dark';
  @property({ attribute: false }) searchResults: Array<{
    id: string; type: string; name: string; path: string;
    line?: number; column?: number; preview?: string; score: number; tags?: string[];
  }> = [];

  override render() {
    return html`
      <div
        class="tab-bar"
        role="tablist"
        aria-label="Panel tabs"
        @dragover=${this._onDragOver}
        @dragleave=${this._onDragLeave}
        @drop=${this._onDrop}
      >
        ${BOTTOM_PANEL_TABS.map(
          (tab) => html`
            <button
              class="pz-tab ${this.activeTab === tab.id ? 'active' : ''}"
              role="tab"
              aria-selected=${this.activeTab === tab.id ? 'true' : 'false'}
              aria-controls="panel-content"
              draggable="true"
              @dragstart=${(e: DragEvent) => this._onDragStart(e, tab.id)}
              @click=${() => this._onTabClick(tab.id)}
            >
              <ark-icon name=${tab.icon} size="xs"></ark-icon>
              ${tab.label}
              ${tab.id === 'diagnostics' ? this._renderDiagnosticBadge() : nothing}
            </button>
          `,
        )}
        <div class="tab-spacer"></div>
        <button class="close-btn" @click=${this._onClose} title="Close Panel">
          <ark-icon name="x" size="xs"></ark-icon>
        </button>
      </div>

      <div class="panel-content" id="panel-content" role="tabpanel" aria-label=${this.activeTab}>
        ${this._renderActiveContent()}
      </div>
    `;
  }

  private _renderDiagnosticBadge() {
    const errors = this.modeler.errorCount;
    const warnings = this.modeler.warningCount;
    if (errors > 0) {
      return html`<span class="badge badge-error" style="margin-left: var(--r5-space-1);">${errors}</span>`;
    }
    if (warnings > 0) {
      return html`<span class="badge badge-warning" style="margin-left: var(--r5-space-1);">${warnings}</span>`;
    }
    return nothing;
  }

  private _renderActiveContent() {
    switch (this.activeTab) {
      case 'diagnostics':
        return html`
          <sysml-diagnostics
            .diagnostics=${this.modeler.diagnostics}
            .sourceText=${this.modeler.sourceText}
            .open=${true}
            .theme=${this.theme}
            @diagnosticReveal=${(e: CustomEvent) =>
              this._emitEvent('diagnostic-reveal', e.detail.range ?? e.detail)}
          ></sysml-diagnostics>
        `;
      case 'search':
        return html`
          <sysml-search
            .searchResults=${this.searchResults}
            @searchQuery=${(e: CustomEvent<{ query: string; filters: Array<{ tag: string; active: boolean }> }>) =>
              this._emitEvent('search-query', e.detail)}
            @resultSelect=${(e: CustomEvent) =>
              this._emitEvent('search-result-select', e.detail)}
          ></sysml-search>
        `;
      case 'solver':
        return html`
          <sysml-solver-panel
            .modeler=${this.modeler}
          ></sysml-solver-panel>
        `;
      case 'history':
        return html`
          <sysml-diff-history-panel
            .modeler=${this.modeler}
          ></sysml-diff-history-panel>
        `;
      case 'simulation':
        return html`
          <sysml-simulation-panel
            .modeler=${this.modeler}
            .theme=${this.theme}
          ></sysml-simulation-panel>
        `;
    }
  }

  private _onTabClick(tab: BottomPanelTab): void {
    this.dispatchEvent(
      new CustomEvent('tab-change', {
        detail: { tab },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onClose(): void {
    this.dispatchEvent(
      new CustomEvent('panel-close', { bubbles: true, composed: true }),
    );
  }

  private _emitEvent(name: string, detail: unknown): void {
    this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true }),
    );
  }

  // -- Drag-and-drop ----------------------------------------------------------

  private _onDragStart(e: DragEvent, tabId: string): void {
    e.dataTransfer?.setData('text/plain', JSON.stringify({ zone: 'bottom', tabId }));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  }

  private _onDragOver(e: DragEvent): void {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    this.classList.add('drop-target');
  }

  private _onDragLeave(): void {
    this.classList.remove('drop-target');
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    this.classList.remove('drop-target');
    try {
      const data = JSON.parse(e.dataTransfer?.getData('text/plain') ?? '{}');
      if (data.zone && data.tabId) {
        this.dispatchEvent(
          new CustomEvent('panel-drop', {
            detail: { sourceZone: data.zone, tabId: data.tabId, targetZone: 'bottom' },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch { /* ignore invalid drag data */ }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-panel-zone': SysmlPanelZone;
  }
}
